/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import axios from "axios";
import DashboardShell from "@/components/dashboard/DashboardShell";
import { StatusPill } from "@/pages/dashboard/TrainerHome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MoreVertical, ShieldAlert, KeyRound, Eye, EyeOff } from "lucide-react";
import { formatApiError } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainersList() {
  const { t } = useLang();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetTarget, setResetTarget] = useState(null);

  const fetchTrainers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/owner/trainers`, { withCredentials: true });
      setTrainers(data);
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTrainers(); }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/owner/trainers/${id}/status`, { status }, { withCredentials: true });
      toast.success(status === "suspended" ? t.app.trainers_list.suspended_toast : status === "active" ? t.app.trainers_list.reactivated_toast : t.app.clients_list.status_updated);
      fetchTrainers();
    } catch (e) {
      toast.error(t.toasts.error_title, { description: formatApiError(e?.response?.data?.detail) });
    }
  };

  return (
    <DashboardShell allowedRoles={["owner"]}>
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-sm font-bold uppercase tracking-[0.2em] text-[#E05A3D]">{t.app.trainers_list.overline}</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#1C3A27]">{t.app.trainers_list.title} ({trainers.length})</h1>
          <p className="mt-1 text-[#5C6B5C]">{t.app.trainers_list.subtitle}</p>
        </div>
        <CreateTrainerDialog onCreated={fetchTrainers} />
      </header>

      <div className="rounded-2xl border border-[#E2E4DF] bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#5C6B5C]">{t.app.common.loading}</div>
        ) : trainers.length === 0 ? (
          <div className="p-10 text-center text-[#5C6B5C]">{t.app.trainers_list.empty}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F0F0EB] text-[#5C6B5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_name}</th>
                <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_email}</th>
                <th className="text-start px-5 py-3 font-semibold">{t.app.trainers_list.th_clients}</th>
                <th className="text-start px-5 py-3 font-semibold">{t.app.clients_list.th_status}</th>
                <th className="text-end px-5 py-3 font-semibold">{t.app.common.actions}</th>
              </tr>
            </thead>
            <tbody>
              {trainers.map((tr) => (
                <tr key={tr.id} data-testid={`trainer-row-${tr.id}`} className="border-t border-[#E2E4DF]">
                  <td className="px-5 py-4 font-medium text-[#1C3A27]">{tr.name}</td>
                  <td className="px-5 py-4 text-[#5C6B5C]">{tr.email}</td>
                  <td className="px-5 py-4">
                    <span className="font-semibold">{tr.active_client_count}</span>
                    <span className="text-[#5C6B5C]"> / {tr.client_count}</span>
                  </td>
                  <td className="px-5 py-4"><StatusPill status={tr.status} /></td>
                  <td className="px-5 py-4 text-end">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`trainer-actions-${tr.id}`} className="p-2 rounded-lg hover:bg-[#F0F0EB] text-[#5C6B5C]">
                          <MoreVertical size={18} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {tr.status !== "suspended" ? (
                          <DropdownMenuItem onClick={() => setStatus(tr.id, "suspended")} data-testid={`trainer-suspend-${tr.id}`} className="text-red-700">
                            <ShieldAlert size={14} className="me-2" /> {t.app.trainers_list.suspend}
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => setStatus(tr.id, "active")} data-testid={`trainer-activate-${tr.id}`}>
                            {t.app.trainers_list.reactivate}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setResetTarget(tr)} data-testid={`trainer-reset-${tr.id}`}>
                          <KeyRound size={14} className="me-2" /> {t.app.trainers_list.reset_password}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ResetPasswordDialog trainer={resetTarget} onClose={() => setResetTarget(null)} onDone={fetchTrainers} />
    </DashboardShell>
  );
}

function CreateTrainerDialog({ onCreated }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const empty = { name: "", email: "", password: "" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/owner/trainers`, form, { withCredentials: true });
      toast.success(t.app.trainers_list.created_toast);
      setForm(empty);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-create-trainer" className="h-12 px-5 rounded-xl bg-[#E05A3D] hover:bg-[#C84B31] text-white font-semibold">
          <Plus size={18} className="me-2" /> {t.app.trainers_list.new_trainer}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t.app.trainers_list.create_title}</DialogTitle>
          <DialogDescription>{t.app.trainers_list.create_desc}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label>{t.app.clients_list.full_name}</Label>
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 h-11 bg-white" />
          </div>
          <div>
            <Label>{t.app.clients_list.email}</Label>
            <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} className="mt-1.5 h-11 bg-white" />
          </div>
          <div>
            <Label>{t.app.trainers_list.password_hint}</Label>
            <Input required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Formation2026!" className="mt-1.5 h-11 bg-white" />
          </div>
          {error && <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>{t.app.common.cancel}</Button>
            <Button type="submit" disabled={submitting} className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">
              {submitting ? t.app.common.creating : t.app.trainers_list.create_btn}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ trainer, onClose, onDone }) {
  const { t } = useLang();
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/owner/trainers/${trainer.id}/reset-password`, { new_password: pw }, { withCredentials: true });
      toast.success(t.app.trainers_list.reset_toast, { description: t.app.trainers_list.reset_toast_desc.replace("{name}", trainer.name) });
      setPw("");
      onClose();
      onDone?.();
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!trainer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{t.app.trainers_list.reset_title}</DialogTitle>
          <DialogDescription>
            {trainer && t.app.trainers_list.reset_desc.replace("{name}", trainer.name)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label>{t.app.trainers_list.new_password}</Label>
            <div className="relative mt-1.5">
              <Input data-testid="reset-trainer-new-pw" type={show ? "text" : "password"} required minLength={10} value={pw} onChange={(e) => setPw(e.target.value)} className="h-11 bg-white pe-10" placeholder={t.app.trainers_list.password_hint} />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C6B5C]">
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t.app.common.cancel}</Button>
            <Button type="submit" disabled={submitting} data-testid="reset-trainer-submit" className="bg-[#1C3A27] hover:bg-[#142A1C] text-white">
              {submitting ? t.app.common.updating : t.app.trainers_list.reset_btn}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
